import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { INLINES } from '@contentful/rich-text-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Helmet } from 'react-helmet';
import './Home.scss'
import HeaderHome from '../components/HeaderHome.js'
import { motion, AnimatePresence } from 'framer-motion'
import { fetchFullScreenPictures, getThumbnailUrlHomePage, getThumbnailDimensions, isVideo, getVideoUrl } from '../utils/imageLoader.js'
import Lenis from 'lenis';

const Home = ({ highlights, fullScreenPictures, biography, buttons }) => {
    const [imagesLoaded, setImagesLoaded] = useState(false);
    const [randomImageIndex, setRandomImageIndex] = useState(null);
    const [highlightLoadedMap, setHighlightLoadedMap] = useState({});
    const [bioLoaded, setBioLoaded] = useState(false);

    // Generate structured data for SEO
    const structuredData = useMemo(() => {
        return {
            "@context": "https://schema.org",
            "@type": "Person",
            "name": "Alex de Brabant",
            "jobTitle": "Artist and photographer",
            "description": "Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology.",
            "url": "https://www.alexdebrabant.com",
            "image": biography?.bioPicture?.url || "",
            "sameAs": [
                // Add social media profiles if available
            ],
            "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": "https://www.alexdebrabant.com"
            }
        };
    }, [biography]);

    // Memoize image URLs to prevent recalculation
    const cachedImageUrls = useMemo(() => {
        if (fullScreenPictures && fullScreenPictures.length > 0) {
            return fullScreenPictures.map(picture => fetchFullScreenPictures(picture));
        }
        return [];
    }, [fullScreenPictures]);

    useEffect(() => {
        document.title = "Alex de Brabant";

        // Check if we have a hash in the URL
        // console.log('Home component mounted, current hash:', window.location.hash);

        // If we have a hash, scroll to the element
        if (window.location.hash === '#bio') {
            console.log('Found #bio hash, scrolling to biography section');
            setTimeout(() => {
                const bioElement = document.getElementById('bio');
                if (bioElement) {
                    console.log('Found bio element, scrolling to it');
                    bioElement.scrollIntoView({ behavior: 'smooth' });
                } else {
                    console.log('Bio element not found');
                }
            }, 100);
        }
    }, []);

    // Lenis smooth scrolling (match other pages)
    useEffect(() => {
        const lenis = new Lenis({
            smoothWheel: true,
            wheelMultiplier: 0.8,
            touchMultiplier: 0.8,
        });

        let rafId;
        const raf = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(raf);
        };
        rafId = requestAnimationFrame(raf);

        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    // Show image immediately without preloading
    useEffect(() => {
        if (cachedImageUrls.length > 0) {
            setImagesLoaded(true);
        }
    }, [cachedImageUrls]);

    // Choose a single random image once on page load
    useEffect(() => {
        if (fullScreenPictures && fullScreenPictures.length > 0 && randomImageIndex === null) {
            const randomIndex = Math.floor(Math.random() * fullScreenPictures.length);
            setRandomImageIndex(randomIndex);
        }
    }, [fullScreenPictures, randomImageIndex]);


    const handleHeroClick = () => {
        const highlightsSection = document.querySelector('.section-home-highlights');
        if (highlightsSection) {
            highlightsSection.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const handleHighlightLoad = (id) => {
        setHighlightLoadedMap(prev => ({ ...prev, [id]: true }));
    };

    const handleHighlightVideoMetadata = (event, id) => {
        const video = event.target;
        if (video && video.videoWidth && video.videoHeight) {
            video.setAttribute('width', video.videoWidth);
            video.setAttribute('height', video.videoHeight);
        }
        setHighlightLoadedMap(prev => ({ ...prev, [id]: true }));
    };


    // Get the random image index
    const getDisplayImageIndex = () => {
        return randomImageIndex !== null ? randomImageIndex : 0;
    };

    return (
        <AnimatePresence mode="wait">
            <motion.main
                className="home-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 1 }}
            >
                <Helmet>
                    <title>Alex de Brabant</title>
                    <meta name="description" content="Alex de Brabant is a Canadian artist and photographer based in Berlin, working at the intersection of art, culture, design, and ecology." />
                    <link rel="canonical" href="https://www.alexdebrabant.com/" />
                    <script type="application/ld+json">
                        {JSON.stringify(structuredData)}
                    </script>
                </Helmet>

                <HeaderHome headerColor="black" backgroundColor={biography?.backgroundColor} buttons={buttons} />

                <section
                    className="section-home-hero"
                    onClick={handleHeroClick}
                    style={{
                        opacity: imagesLoaded ? 1 : 0,
                        transition: 'opacity 1s ease-in-out'
                    }}
                    aria-label="Featured artwork gallery"
                >
                    {fullScreenPictures && fullScreenPictures.length > 0 && randomImageIndex !== null && cachedImageUrls.length > 0 ? (
                        <>
                            {/* Display single random image */}
                            {cachedImageUrls.map((url, index) => (
                                <img
                                    key={`hero-img-${index}`}
                                    src={url}
                                    alt={fullScreenPictures[index]?.title || "Alex de Brabant artwork"}
                                    className="hero-centered-image"
                                    fetchpriority={getDisplayImageIndex() === index ? "high" : "low"}
                                    style={{
                                        opacity: getDisplayImageIndex() === index ? 1 : 0,
                                        pointerEvents: getDisplayImageIndex() === index ? 'auto' : 'none'
                                    }}
                                />
                            ))}
                        </>
                    ) : null}
                </section>

                <section className="section-home-highlights" id="section-home-highlights" aria-labelledby="highlights-heading">
                    <h2 id="highlights-heading" className="sr-only">Featured Projects</h2>
                    {highlights.map((highlight) => (
                        <Link
                            key={highlight.id}
                            to={`/${highlight.url}`}
                            className="home-card"
                        >
                            {highlight.picture && (
                                isVideo(highlight.picture) ? (
                                    <video
                                        className="home-img"
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        onLoadedMetadata={(e) => handleHighlightVideoMetadata(e, highlight.id)}
                                        style={{ opacity: highlightLoadedMap[highlight.id] ? 1 : 0, transition: 'opacity 0.4s ease-in-out' }}
                                    >
                                        <source src={getVideoUrl(highlight.picture)} type="video/mp4" />
                                        Your browser does not support the video tag.
                                    </video>
                                ) : (
                                    <img
                                        src={getThumbnailUrlHomePage(highlight.picture)}
                                        alt={`${highlight.title} - artwork preview`}
                                        loading="lazy"
                                        width={getThumbnailDimensions(highlight.picture).width}
                                        height={getThumbnailDimensions(highlight.picture).height}
                                        className="home-img"
                                        onLoad={() => handleHighlightLoad(highlight.id)}
                                        style={{ opacity: highlightLoadedMap[highlight.id] ? 1 : 0, transition: 'opacity 0.4s ease-in-out' }}
                                    />
                                )
                            )}
                            <p className="home-label">
                                {highlight.title}
                            </p>
                        </Link>
                    ))}
                </section>


                <section className="home-info-section" id="bio" aria-labelledby="bio-heading">
                    <div className="info-column">
                        <div className="info-text" style={{ color: 'black' }}>
                            <h2 id="bio-heading" className="sr-only">Biography</h2>
                            {biography?.bioText && documentToReactComponents(biography.bioText, {
                                renderText: text =>
                                    text.split('\n').reduce((children, textSegment, index) => {
                                        return [...children, index > 0 && <br key={index} />, textSegment];
                                    }, []),
                                renderNode: {
                                    [INLINES.HYPERLINK]: (node, children) => {
                                        const { uri } = node.data;
                                        return (
                                            <a href={uri} target="_blank" rel="noopener noreferrer">
                                                {children}
                                            </a>
                                        );
                                    },
                                },
                            })}
                        </div>
                    </div>
                    <div className="info-column">
                        <div className="info-image">
                            {biography?.bioPicture && (
                                <img
                                    src={biography?.bioPicture?.url}

                                    alt={biography?.bioPicture?.title || "Portrait of Alex de Brabant"}
                                    loading="lazy"

                                    onLoad={() => setBioLoaded(true)}
                                    style={{ opacity: bioLoaded ? 1 : 0, transition: 'opacity 0.4s ease-in-out' }}
                                />
                            )}
                        </div>
                    </div>
                </section>
            </motion.main>
        </AnimatePresence>


    )
}

export default Home